import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import { mapActions, mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import sumBy from 'lodash/sumBy'
import { CustomFieldsForm } from '@kwixeo/custom-fields-vue'
import CatalogElementMixin from '../../mixins/CatalogElementMixin'
import { fetchProduct, needFetchProduct } from '../../fetchDatas/product'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { KForm, KFormInput, KFormSelect, KFormEditor, KFormNumber, KFormSpinner, KFormCheckbox } from '@kwixeo/form-vue'
import { emitter } from '@kwixeo/interface-vue'
import { AxiosResponse } from 'axios'
import {
  BFormRow,
  BCol,
  BFormGroup,
  BRow,
  BInputGroup,
  BTabs,
  BTab,
  BFormCheckbox,
  BInputGroupAppend,
  BFormRadioGroup,
  BFormRadio,
  VBTooltip,
  BButton,
  BInputGroupText,
  BAlert
} from 'bootstrap-vue'
import CatalogSearch from '../CatalogSearch/CatalogSearch.vue'

@Component({
  methods: {
    ...mapActions('dynamicModals', ['openModal']),
    ...mapActions('catalog', ['loadChargeCategories', 'loadVariationLevels'])
  },
  computed: {
    ...mapState('catalog', ['chargeCategories', 'variationLevels'])
  },
  components: {
    KFormCheckbox,
    FontAwesomeIcon,
    KForm, KFormInput, KFormSelect, KFormEditor, KFormNumber, KFormSpinner,
    BFormRow, BCol, BFormGroup, BRow, BInputGroup, BTabs, BTab, BFormCheckbox, BInputGroupAppend, BFormRadioGroup,
    BFormRadio, BButton, BInputGroupText, BAlert,
    CustomFieldsForm,
    CatalogSearch
  },
  directives: {
    'b-tooltip': VBTooltip
  }
})
export default class ProductForm extends Mixins(CatalogElementMixin) {
  openModal!: (params: string|KwixeoInterface.OpenModalParamObject) => void
  loadChargeCategories!: (force?: boolean) => void
  chargeCategories!: KwixeoCatalog.ChargeCategories
  loadVariationLevels!: (force?: boolean) => void
  variationLevels!: KwixeoCatalog.VariationLevels

  @Prop({ default: null }) value!: KwixeoCatalog.Product|null
  @Prop({ default: false }) isGroup!: KwixeoCatalog.Product|null

  protected localElement: KwixeoCatalog.FormProduct = {
    designation: null,
    status: { active: true },
    not_for_sale: false,
    variations_length: 0,
    providers_references: [],
    customers_price: [],
    unit_buy_price_ht: 0,
    unit_price_ht: 0,
    unit_price_tva: 0,
    unit_price_ttc: 0,
    default_quantity: 1,
    formStatus: 2,
    with_stock: false,
    stock_tracking_mode: 0,
    cant_change_price: false,
    custom_price_mode: 'quantity',
    custom_fields: [],
    group_contents: []
  }

  get status (): KwixeoCatalog.FormStatus[] {
    const status: KwixeoCatalog.FormStatus[] = [
      { id: 1, label: this.$t('catalog.form.status.disable') },
      { id: 2, label: this.$t('catalog.form.status.enable') }
    ]

    if (!this.isGroup) {
      status.push({ id: 3, label: this.$t('catalog.form.status.notForSale') })
    }

    return status
  }

  private traceabilities = [
    { id: 0, label: this.$t('catalog.form.traceabilities.no') },
    { id: 1, label: this.$t('catalog.form.traceabilities.lot') },
    { id: 2, label: this.$t('catalog.form.traceabilities.serial') }
  ]

  private variationLevelsLength = [
    { value: 0, label: this.$t('catalog.form.variationLevels.no') },
    { value: 1, label: this.$tc('catalog.form.variationLevels.level', 1) },
    { value: 2, label: this.$tc('catalog.form.variationLevels.level', 2) },
    { value: 3, label: this.$tc('catalog.form.variationLevels.level', 3) }
  ]

  private providersCache: KwixeoForm.SelectAjaxCache = {}

  async created (): Promise<void> {
    if (this.id) {
      const product = await fetchProduct(this.id)
      this.setLocalElement(product)
      this.loading = false
    } else {
      if (this.teamConfigs.stock_new_product_state) {
        this.localElement.with_stock = true
      }
    }
  }

  async mounted (): Promise<void> {
    await this.loadChargeCategories()

    if (this.hasModule('catalog-variations')) {
      await this.loadVariationLevels()
    }
  }

  // Return form url
  get formAction (): string {
    let url = '/catalog/product'
    if (this.localElement.id) {
      url += '/' + this.localElement.id
    }
    return url
  }

  get providerApiUrl (): string {
    return '/customer?sort=name&filter[is_provider]=1'
  }

  // Return the average costs of providers reference
  get averageProviderUnitPrice (): number {
    if (this.localElement.is_group) {
      return sumBy(this.localElement.group_contents, content => this.roundNumber(this.number(content.quantity) * this.number(content?.catalog?.element?.total_costs), 2))
    }

    const references = this.localElement.providers_references ?? []
    const total = references.map(ref => this.calculProviderReferenceTotalLine(ref) * 100)
      .reduce((a: number, b: number) => a + (b || 0))

    return Math.round(total / references.length) / 100
  }

  @Watch('localElement.providers_references', { deep: true})
  private watchProvidersReferences (): void {
    if (this.localElement.providers_references.length === 1) {
      this.localElement.unit_buy_price_ht = this.averageProviderUnitPrice
    }
  }

  private changeUnitPrice (): void {
    if (this.localElement.providers_references.length === 1) {
      this.localElement.providers_references[0].buy_price_ht = this.localElement.unit_buy_price_ht as number - (this.localElement.providers_references[0].various_costs ?? 0)
    }
  }

  // Return the amount of all costs
  get totalCosts (): number {
    const price = !this.localElement.providers_references || this.localElement.providers_references.length === 0 ?
      this.number(this.localElement.unit_buy_price_ht) :
      this.averageProviderUnitPrice

    return +price + +this.number(this.localElement.various_costs)
  }

  // Return the vat rate to use
  get vatRate (): number {
    return this.localElement.vat_rate ? this.number(this.localElement.vat_rate.rate) : 0
  }

  get variationLevelValue (): (level: string|undefined|null|KwixeoCatalog.VariationLevel) => null|KwixeoCatalog.VariationLevel|string {
    return (level: string|undefined|null|KwixeoCatalog.VariationLevel) => {
      if (level === undefined || level === null) {
        return null
      } else if (typeof level === 'string') {
        return level
      } else {
        return level.label
      }
    }
  }

  get hasVariations (): boolean {
    return this.number(this.localElement.variations_length) > 0 && this.hasModule('catalog-variations')
  }

  protected async checkElementDatas (element: KwixeoCatalog.Product): Promise<KwixeoCatalog.Product> {
    if (element.id && needFetchProduct(element)) {
      return fetchProduct(element.id)
    }
    return element
  }

  // Set the local product
  protected setLocalElement (product: KwixeoCatalog.Product): void {
    const localElement: KwixeoCatalog.FormProduct = JSON.parse(JSON.stringify(product))

    // Set the local status
    localElement.formStatus = product.not_for_sale ? 3 : product.status && product.status.active ? 2 : 1

    if (!localElement.providers) {
      localElement.providers = []
    }
    if (!localElement.providers_references) {
      localElement.providers_references = []
    }
    if (!localElement.custom_fields) {
      localElement.custom_fields = []
    }
    if (!localElement.group_contents) {
      localElement.group_contents = []
    }

    this.localElement = localElement
  }

  // Add a provider reference
  protected addNewProviderReference (): void {
    this.localElement.providers_references.push({})
  }

  // Remove a provider reference
  protected deleteProviderReference (index: number): void {
    this.localElement.providers_references.splice(index, 1)
  }

  // Caculate total costs for a product for a provider line
  protected calculProviderReferenceTotalLine (reference: Partial<KwixeoCatalog.ProviderReference>): number {
    const unit_price = this.number(reference.buy_price_ht)
    const various_costs = this.number(reference.various_costs)
    return unit_price + various_costs
  }

  // Calculate eco tax amounts from ht
  protected calculEcoTaxHT (val: number): void {
    const tva = val * this.vatRate / 100
    const ttc = tva + val
    this.localElement.eco_tax_tva = tva
    this.localElement.eco_tax_ttc = ttc
  }

  // Calculate eco tax amounts from ttc
  protected calculEcoTaxTTC (val: number): void {
    const ht = val / ((this.vatRate / 100) + 1)
    this.localElement.eco_tax_tva = val - ht
    this.localElement.eco_tax_ht = ht
  }

  // Validate datas before submit form
  protected validateFormDatas (): KwixeoForm.AjaxValidationErrors|boolean {
    const errors: KwixeoForm.AjaxValidationErrors = {}

    // Check providers references
    if (!this.hasVariations && this.localElement.providers_references.length > 0) {
      const providers = Array.from(this.localElement.providers_references)
      const duplicates: number[] = []
      providers.forEach((ref, idx) => {
        const duplicate = providers.find((v, i) => {
          return v.customer && ref.customer && v.customer.id === ref.customer.id && v.reference === ref.reference && i !== idx
        })
        if (duplicate !== undefined) {
          duplicates.push(idx)
        }
      })

      duplicates.forEach(i => {
        errors['providers_references.' + i + '.reference'] = [ this.$t('catalog.form.duplicateProviderReferenceError') as string ]
      })
    }

    // Check variations
    if (this.localElement.variations_length > 1) {
      const variations = [this.variationLevelValue(this.localElement.variation_level1), this.variationLevelValue(this.localElement.variation_level2)]
      if (this.localElement.variations_length === 3) {
        variations.push(this.variationLevelValue(this.localElement.variation_level3))
      }
      const duplicates: number[] = []
      variations.forEach((variation, idx) => {
        const duplicate = variations.find((v, i) => {
          return v && variation && v === variation && i !== idx
        })
        if (duplicate !== undefined) {
          duplicates.push(idx)
        }
      })

      duplicates.forEach(i => {
        errors['variation_level' + (i + 1)] = [ this.$t('catalog.form.duplicateVariationLevelError') as string ]
      })
    }

    if (isEmpty(errors)) {
      return true
    }

    return errors
  }

  // Create a new provider
  protected newProvider (target: KwixeoCustomers.Customer|KwixeoCustomers.Customers): void {
    this.openModal({
      key: 'provider-form',
      attributes: {
        withRedirection: false
      },
      submitted: response => {
        const provider = response.data
        this.providersCache.options?.push(provider)
        if (Array.isArray(target)) {
          target.push(provider)
        } else {
          target = provider
        }
      }
    })
  }

  private submitComplete (response: AxiosResponse): void {
    const product: KwixeoCatalog.Product = response.data
    if (this.value && this.value.id) {
      emitter.emit('productUpdated', { old: this.value, new: product })
    } else {
      emitter.emit('productCreated', product)
    }
  }

  private referenceUpdated (reference: string|null): void {
    if (!reference) {
      return
    }

    this.localElement.providers_references.forEach(provider => {
      if (!provider.reference) {
        provider.reference = reference
      }
    })
  }

  private addGroupContent (content: KwixeoInterface.SearchResultData): void {
    const element = content.data as (KwixeoCatalog.Product|KwixeoCatalog.Service)

    const contents = this.localElement.group_contents
    if (!contents) {
      return
    }
    const index = contents.findIndex(val => val.catalog.type === content.type && val.catalog.id === element.id)
    if (index >= 0) {
      contents[index].quantity = Number(contents[index].quantity) + 1
    } else {
      contents.push({
        catalog: {
          id: element.id,
          type: content.type,
          element: element,
        },
        unit_costs: element.total_costs,
        quantity: 1,
        total_costs: element.total_costs,
      })
    }
  }

  private deleteGroupContent (index: number): void {
    this.localElement.group_contents?.splice(index, 1)
  }

  get groupContentTotalCost (): (index: number) => number {
    return index => {
      const content = (this.localElement.group_contents as KwixeoCatalog.GroupContents)[index]
      return this.roundNumber(Number(content.catalog.element.total_costs) * Number(content.quantity), 2)
    }
  }
}
